import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Lenis from "lenis";
import Nav from "@/components/Nav";
import Host from "@/components/Host";
import Footer from "@/components/Footer";
import Transition from "@/components/Transition";
import SearchOverlay from "@/components/SearchOverlay";
import SavedDrawer from "@/components/SavedDrawer";
import Gate from "@/views/Gate";
import Haat from "@/views/Haat";
import Shop from "@/views/Shop";
import Moncho from "@/views/Moncho";
import AdminPanel from "@/admin/AdminPanel";
import CreatorPanel from "@/creator/CreatorPanel";
import { HOST_LINES } from "@/data";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: false };
  }
  static getDerivedStateFromError() {
    return { err: true };
  }
  render() {
    if (this.state.err) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-dusk p-8 text-center font-display text-xl text-sand">
          কিছু একটা গোলমাল হয়েছে — একটু পরে আবার আসুন।
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  const [view, setView] = useState("gate");
  const [shopKey, setShopKey] = useState("potchitra");
  // সন্ধ্যা ৫টার পর বা ভোর ৫টার আগে — গ্রাম নিজে থেকেই প্রদীপের আলোয় নামে
  const [night, setNight] = useState(() => {
    const saved = localStorage.getItem("rom_night");
    if (saved === null) {
      const h = new Date().getHours();
      return h >= 17 || h < 5;
    }
    return saved === "1";
  });
  const [trans, setTrans] = useState(null);
  const [saved, setSaved] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("rom_saved") || "[]");
    } catch {
      return [];
    }
  });
  const [hostMsg, setHostMsg] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const lenisRef = useRef(null);
  const [site, setSite] = useState(null);

  // অ্যাডমিন-প্যানেলে সাধা কনটেন্ট — দর্শকের পোর্টালে সঙ্গে সঙ্গে
  const loadSite = async () => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site`);
      if (r.ok) setSite(await r.json());
    } catch {}
  };
  useEffect(() => {
    loadSite();
  }, []);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("rom_saved", JSON.stringify(saved));
  }, [saved]);

  useEffect(() => {
    localStorage.setItem("rom_night", night ? "1" : "0");
  }, [night]);

  useEffect(() => {
    if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
    const line = HOST_LINES[view];
    setHostMsg(line ? { ...line, autoKey: Date.now() } : null);
  }, [view]);

  const navTo = (target, label, shop) => {
    if (shop) setShopKey(shop);
    if (target === view && !(shop && shop !== shopKey)) return;
    setTrans({ label, key: Date.now() });
    setTimeout(() => setView(target), 620);
    setTimeout(() => setTrans(null), 1250);
  };

  const toggleSave = (id) =>
    setSaved((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  // দিন / গোধূলি / রাত — গ্রামের বর্তমান আবহ
  const h = new Date().getHours();
  const phase = !night ? "day" : h >= 17 && h < 19 ? "dusk" : "night";

  return (
    <ErrorBoundary>
      <div className={`night-wrap min-h-screen overflow-x-clip bg-dusk font-body text-sand ${night ? "night" : ""}`}>
        {view !== "admin" && view !== "creator" && (
          <Nav
            view={view}
            navTo={navTo}
            onSearch={() => setSearchOpen(true)}
            onDrawer={() => setDrawerOpen(true)}
            savedCount={saved.length}
            night={night}
            onToggleNight={() => setNight((n) => !n)}
          />
        )}

        <AnimatePresence mode="wait">
          <motion.main
            key={view + shopKey}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            {view === "gate" && <Gate navTo={navTo} phase={phase} site={site} />}
            {view === "hat" && <Haat navTo={navTo} site={site} />}
            {view === "shop" && <Shop navTo={navTo} shopKey={shopKey} saved={saved} toggleSave={toggleSave} site={site} />}
            {view === "moncho" && <Moncho navTo={navTo} />}
            {view === "admin" && (
              <AdminPanel
                onExit={() => navTo("gate", "গ্রাম-প্রবেশ")}
                onSaved={loadSite}
                goCustomer={(v, l, shop) => navTo(v, l, shop)}
              />
            )}
            {view === "creator" && (
              <CreatorPanel onExit={() => navTo("gate", "গ্রাম-প্রবেশ")} onSaved={loadSite} goCustomer={(v, l) => navTo(v, l)} />
            )}
            <Footer onAdmin={() => navTo("admin", "প্রশাসক-প্যানেল")} onCreator={() => navTo("creator", "শিল্পী-প্যানেল")} />
          </motion.main>
        </AnimatePresence>

        {(view !== "admin" && view !== "creator" && !(view === "shop" && shopKey === "potchitra")) && (
          <Host message={hostMsg} navTo={navTo} scene={view} />
        )}

        <SearchOverlay
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          navTo={(v, l) => {
            setSearchOpen(false);
            navTo(v, l);
          }}
        />

        <SavedDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          saved={saved}
          toggleSave={toggleSave}
          navTo={(v, l) => {
            setDrawerOpen(false);
            navTo(v, l);
          }}
        />

        <AnimatePresence>
          {trans && <Transition key={trans.key} label={trans.label} />}
        </AnimatePresence>

        <div className="grain-overlay" aria-hidden="true" />
      </div>
    </ErrorBoundary>
  );
}
